import { requestFCMToken, subscribeToForegroundMessages } from "@/lib/firebase"
import { setStoredPushDeviceRegistration } from "@/lib/device-id-storage"
import { findCurrentPushDevice } from "@/lib/push-devices"
import { useEffect, useRef } from "react"

import {
  getDevices,
  registerDevice,
  type DeviceResponse,
} from "@/services/devices"
import { useNotificationContext } from "@/contexts/notification-context"
import { useAuth } from "@/contexts/auth-context"

export interface UsePushNotificationsOptions {
  /** Whether the user is authenticated and profile is ready. */
  isReady: boolean
  /** Refetch user profile after registering device. */
  refetchProfile: () => Promise<void>
}

export interface RegisterCurrentPushDeviceParams {
  address?: string
  refetchProfile: () => Promise<void>
}

/**
 * Registers the current browser for push if not already in /v1/devices
 * for the same app/platform + FCM token. Silent: no toast on success/error.
 */
export async function registerCurrentPushDevice({
  address,
  refetchProfile,
}: RegisterCurrentPushDeviceParams): Promise<boolean> {
  const token = await requestFCMToken()
  if (!token) return false

  let devices: DeviceResponse[] = []
  try {
    const response = await getDevices()
    devices = response?.data ?? []
  } catch {
    // If device list can't be fetched, continue with register attempt.
  }

  const currentDevice = findCurrentPushDevice(devices, token)
  if (currentDevice) {
    setStoredPushDeviceRegistration(address, {
      id: currentDevice.id,
      token,
    })
    return true
  }

  try {
    const res = await registerDevice({
      device_token: token,
    })
    if (res?.data?.id) {
      setStoredPushDeviceRegistration(address, {
        id: res.data.id,
        token,
      })
    }
    await refetchProfile()
    return true
  } catch (err) {
    const msg =
      err && typeof err === "object" && "message" in err
        ? String((err as { message: unknown }).message)
        : ""
    if (msg.includes("422") || msg.includes("already registered")) {
      await refetchProfile()
      return true
    }
    return false
  }
}

export function usePushNotifications({
  isReady,
  refetchProfile,
}: UsePushNotificationsOptions) {
  const hasTriedRegisterRef = useRef(false)
  const lastAddressRef = useRef<string | undefined>(undefined)
  const { addNotification } = useNotificationContext()
  const { address } = useAuth()

  useEffect(() => {
    if (typeof window === "undefined" || !("Notification" in window)) return
    if (!isReady) return

    const addr = address?.toLowerCase()
    if (lastAddressRef.current !== addr) {
      lastAddressRef.current = addr
      hasTriedRegisterRef.current = false
    }
    if (hasTriedRegisterRef.current) return

    let cancelled = false
    hasTriedRegisterRef.current = true

    async function register() {
      const didRegister = await registerCurrentPushDevice({
        address,
        refetchProfile,
      })
      if (cancelled || didRegister) return
    }

    register()
    return () => {
      cancelled = true
    }
  }, [isReady, address, refetchProfile])

  useEffect(() => {
    if (!isReady) return
    const unsubscribe = subscribeToForegroundMessages((payload) => {
      const notification = payload.notification
      const data = (payload.data as Record<string, unknown>) || {}
      const type = (data.type as string) || "info"
      const title = notification?.title ?? "Notification"
      const body = notification?.body ?? ""
      let rawPayload: string | undefined
      try {
        rawPayload = JSON.stringify({
          notification: payload.notification,
          data: payload.data,
        })
      } catch {
        rawPayload = undefined
      }
      addNotification({
        type,
        title,
        description: body || undefined,
        data,
        rawPayload,
      })
    })
    return () => {
      unsubscribe?.()
    }
  }, [isReady, addNotification])
}
